import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
    AppBar,
    Box,
    Button,
    CssBaseline,
    Divider,
    Grid,
    IconButton,
    Toolbar,
    Typography
} from "@mui/material/";
import { ChevronLeft, Menu } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import actions from "../../../Store/actions";
import { loginRequest } from "../../../config/authConfig";
import { useAuthenticated } from "../../hooks";
import { strings } from "../../i18n";
import Drawer from "../Drawer";
import Toaster from "../Toaster";
import UserMenu from "../UserMenu";
import useDrawerItems from "./use-drawer-items";
import styles, { activeMenuStyle } from "./styles";
import ResponsiveImage from "../ResponsiveImage";
import { logToSentry } from "../../../config/Sentry/";

type Props = {
    children: React.ReactNode
};

/**
 * App Screen Component
 * @param {Props} props
 */

export default ({ children }: Props) => {
    const userOrganisations = useSelector((state) => state.userOrganisations);
    const appSettings = useSelector((state) => state.appSettings);
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const drawerItems = useDrawerItems();

    const { hasIdToken, isExpired, isDenied } = useAuthenticated();
    const location = useLocation();
    const { instance } = useMsal();

    const [activeKey, setActiveKey] = useState(null);

    const { drawerOpen } = appSettings;
    useEffect(() => {
        if (location.pathname.split("/")[2] === "allasset")
            setActiveKey("assets");
        else setActiveKey(location.pathname.split("/")[2]);
    }, [location]);

    const onDrawerClose = () =>
        dispatch(
            actions.appSettings.create.set({
                drawerOpen: false
            })
        );

    const onDrawerToggle = useCallback(() => {
        try {
            dispatch(
                actions.appSettings.create.set({ drawerOpen: !drawerOpen })
            );
        } catch (error) {
            logToSentry("Toggling drawer failed", { error });
        }
    }, [drawerOpen, dispatch]);

    // Hand straight off to Azure B2C rather than the local /login route, which
    // bounces back to /auth while MSAL still holds an account. This app never
    // collects credentials itself.
    const onLoginClick = () =>
        instance.loginRedirect(loginRequest).catch((error) => {
            logToSentry("On app bar login:", {
                error: {
                    name: error?.name,
                    message: error?.message,
                    errorCode: error?.errorCode
                }
            });
        });

    useEffect(() => {
        /** Name of the current tab */
        const name = location.pathname.split("/").pop();
        const heading = isNaN(name) ? name : "";
        dispatch(
            actions.appSettings.create.set({ appBarTitle: strings(heading) })
        );
    }, [location]);

    return (
        <>
            <CssBaseline />
            <div className={classes.root}>
                <AppBar
                    className={classes.appBar}
                    color="primary"
                    elevation={0}
                    position="fixed"
                >
                    <Toolbar disableGutters>
                        <Grid container>
                            <Grid item xs={2} alignContent={"center"}>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <IconButton
                                        className={classes.menuContainer}
                                        onClick={onDrawerToggle}
                                        sx={{
                                            display: { xs: "flex", md: "none" }
                                        }}
                                        size="large"
                                        edge="start"
                                    >
                                        {drawerOpen ? (
                                            <ChevronLeft color="primary" />
                                        ) : (
                                            <Menu color="primary" />
                                        )}
                                    </IconButton>
                                    {userOrganisations.item.logoPath &&
                                    userOrganisations.item.logoPath.length >
                                        1 ? (
                                        <Box
                                            sx={{
                                                width: 125,
                                                height: 40,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                overflow: "hidden", // Ensures any excess is cropped
                                                borderRadius: "4px" // Optional: rounds the corners
                                            }}
                                        >
                                            <ResponsiveImage
                                                src={
                                                    userOrganisations.item
                                                        .logoPath
                                                }
                                                alt={
                                                    userOrganisations.item.name
                                                }
                                            />
                                        </Box>
                                    ) : (
                                        <Typography variant="overline">
                                            {userOrganisations.item.name}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={8}>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: {
                                            xs: "none",
                                            md: "flex"
                                        }
                                    }}
                                >
                                    {drawerItems.map((item) => {
                                        if (item.isAdmin && !item.disabled) {
                                            return (
                                                <Button
                                                    key={item.key}
                                                    onClick={() => {
                                                        const targetPath =
                                                            item.path;
                                                        const isOnCreatePage =
                                                            location.pathname.startsWith(
                                                                "/auth/audits/create"
                                                            );
                                                        const isLeavingCreate =
                                                            isOnCreatePage &&
                                                            !targetPath?.startsWith(
                                                                "/auth/audits/create"
                                                            );

                                                        if (isLeavingCreate) {
                                                            dispatch(
                                                                actions.auditsCreate.create.itemSet(
                                                                    {
                                                                        pendingNavigation:
                                                                            item.path,
                                                                        leavingAudit: true
                                                                    }
                                                                )
                                                            );
                                                            return;
                                                        }

                                                        setActiveKey(item.key); // Set active button key on click
                                                        item.onClick(); // Trigger the button's onClick action
                                                    }}
                                                    sx={activeMenuStyle(
                                                        activeKey === item.key
                                                    )}
                                                    size="medium"
                                                >
                                                    {item.primary}
                                                </Button>
                                            );
                                        }
                                    })}
                                </Box>
                            </Grid>
                            <Grid item xs={3} />
                        </Grid>
                        {hasIdToken && !isDenied ? (
                            <UserMenu />
                        ) : (
                            <Button color="inherit" onClick={onLoginClick}>
                                {strings("login")}
                            </Button>
                        )}
                    </Toolbar>
                    <Divider />
                </AppBar>
                <Drawer
                    disabled={!hasIdToken || isExpired}
                    items={drawerItems}
                    onClose={onDrawerClose}
                    onOpen={onDrawerToggle}
                    open={drawerOpen}
                />
                <div className={classes.main}>
                    <div className={classes.content}>{children}</div>
                </div>
            </div>
            <Toaster />
        </>
    );
};
